import { Component, ChangeDetectionStrategy, input, model, output, signal, computed } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';

import { AvailableIcons, ApIconComponent } from '@ap/icon';

import { ChipRole } from './chip.model';

@Component({
    selector: 'ap-chip',
    templateUrl: './chip.component.html',
    styleUrls: ['./chip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ApIconComponent, NgTemplateOutlet, NgClass],
    host: {
        /* eslint-disable @typescript-eslint/naming-convention */
        '[attr.role]': 'role()',
        '[attr.aria-selected]': 'ariaSelected()',
        '[attr.aria-checked]': 'ariaChecked()',
        '[attr.tabindex]': 'isDismissable() ? null : 0',
        '(click)': 'toggleSelected()',
        '(keydown.enter)': 'toggleSelected()',
        '(keydown.space)': 'toggleSelected()'
        /* eslint-enable @typescript-eslint/naming-convention */
    }
})
export class ApChipComponent {
    public readonly role = signal<ChipRole | null>(null);
    public readonly selectionChange = output<boolean>();

    /**
     * Whether component can be removed.
     * If chip is dismissible it can not be selected.
     * It can only be removed by clicking on the 'X' icon
     */
    public readonly isDismissable = input(false);

    /**
     * Is Chip selected will apply selected styling to chip.
     * Does not work if `isDismissable` is set to true, as dismissible chips can not be selected
     */
    public readonly selected = model(false);

    /**
     * Icon name of icon to show in the chip (before the text).
     * Can use any icon available in `ap-icon`
     */
    public readonly iconName = input<AvailableIcons>();

    /**
     * Emits when `X` button is clicked on dismissible / dynamic chip
     */
    public readonly dismiss = output<void>();

    /**
     * Toggle selected state.
     * Can only deselect if Chip role is option (allowing selection of multiple or no chips)
     */
    public toggleSelected(): void {
        if (this.isDismissable()) {
            return;
        }

        // Do not deselect if role is list item or radio
        const selected = this.selected();
        if ((this.role() === ChipRole.LISTITEM || this.role() === ChipRole.RADIO) && selected) {
            return;
        }
        this.selected.set(!selected);
        this.selectionChange.emit(!selected);
    }

    /**
     * An element with `[role=radio]` requires the `[aria-checked]` attribute, even when false.
     * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/radio_role
     * @private
     */
    protected readonly ariaChecked = computed(() => {
        return this.role() === ChipRole.RADIO ? this.selected() || false : null;
    });

    /**
     * An element with `[role=option]` requires the `[aria-selected]` attribute, even when false.
     * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/option_role
     * @private
     */
    protected readonly ariaSelected = computed(() => {
        return this.role() === ChipRole.OPTION ? this.selected() || false : null;
    });
}
